import type { v2 } from '@deque/billing-service-client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '@deque/cauldron-react';
import { IntegrationLogo } from '~/app/components/integrations/IntegrationLogo';
import { getJiraOnPremActions } from '~/app/utils/getJiraOnPremActions';
import analyticsInstances, {
  getFallbackAnalyticsInstanceId
} from '~/common/analyticsInstances';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import {
  ProductSlugs,
  SupportedIntegrationProductSlugs
} from '~/common/constants';
import { type AuthUser } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import { useProducts } from '~/common/contexts/products';
import {
  InterWindowMessageType,
  type InterWindowMessage
} from '~/common/types';
import ConnectionCard from '../ConnectionCard';
import { type ProductSlugWithConnections } from '../IntegrationConfigurations';
import styles from '../IntegrationConfigurations.css';

export interface JiraOnPremIntegrationConfigurationsProps {
  connectionByProductSlug: ProductSlugWithConnections;
  activeEnterprise: v2.Enterprise;
  isEnterpriseAdmin: boolean;
  authUser: AuthUser;
  reloadConnections: () => void;
}

function JiraOnPremIntegrationConfigurations({
  connectionByProductSlug,
  activeEnterprise,
  isEnterpriseAdmin,
  authUser,
  reloadConnections
}: JiraOnPremIntegrationConfigurationsProps) {
  const { t } = useTranslation();

  const { getProductBySlug } = useProducts();
  const { setContents } = useGlobalToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleInterWindowMessage = (event: MessageEvent) => {
      if (event.origin !== location.origin) {
        return;
      }

      if (event.data && event.data.message_type) {
        const messagePayload: InterWindowMessage = event.data;

        if (
          messagePayload.message_type ===
            InterWindowMessageType.REFRESH_INTEGRATION_CONNECTION &&
          messagePayload.data.product_slug ===
            SupportedIntegrationProductSlugs.jiraOnPremIntegration
        ) {
          // This the message we are looking for - we will handle
          if (setContents) {
            const { displayName } =
              connectionByProductSlug.integrationProductInfo;
            setContents(
              t('Successfully connected to {{integrationType}}', {
                integrationType: displayName
              }),
              'confirmation'
            );
          }

          const analytics =
            analyticsInstances[
              getFallbackAnalyticsInstanceId(
                connectionByProductSlug.integrationProductInfo
                  .integrationProductSlug
              )
            ];
          analytics.integrationConnect({
            integration: connectionByProductSlug.integrationProductInfo
              .integrationProductSlug as unknown as SupportedIntegrationProductSlugs
          });

          // refresh list of connections
          reloadConnections();
        }
      }
    };

    window.addEventListener('message', handleInterWindowMessage, false);

    return () => {
      window.removeEventListener('message', handleInterWindowMessage, false);
    };
  }, []);

  useEffect(() => {
    if (error) {
      setContents(error, 'error');
    }
  }, [error, setContents]);

  if (isProcessing) {
    return <ScrimmedLoader label={t('Loading...')} />;
  }

  const { integrationProductInfo, productSlug } = connectionByProductSlug;
  const integrationDisplayName = integrationProductInfo.displayName;
  const analyticsInstance =
    analyticsInstances[
      getFallbackAnalyticsInstanceId(
        integrationProductInfo.integrationProductSlug
      )
    ];

  const { connect: addNewConnection } = getJiraOnPremActions({
    displayName: integrationDisplayName,
    integrationProductInfo,
    connection: null,
    authUser,
    enterprise: activeEnterprise,
    analytics: analyticsInstance,
    setIsProcessing,
    setError,
    connectionsUpdated: reloadConnections,
    setContents,
    t
  });

  return (
    <section className={styles.integrationSection}>
      <div>
        <div className={styles.productItemWrapV2}>
          <div className={styles.productLogoV2}>
            <IntegrationLogo
              integrationProductSlug={
                productSlug as unknown as SupportedIntegrationProductSlugs
              }
            />
          </div>

          <div className={styles.headerContent}>
            <h3>{integrationDisplayName}</h3>

            <p>
              {t(
                'Boost your team’s accessibility efforts with seamless issue tracking directly from {{devtoolsProductName}} to {{integrationDisplayName}}',
                {
                  devtoolsProductName: getProductBySlug(
                    ProductSlugs.axeDevToolsExtension
                  )?.name,
                  integrationDisplayName
                }
              )}
            </p>
            {isEnterpriseAdmin && (
              <Button
                variant="primary"
                onClick={addNewConnection}
                aria-label={t(
                  'Add new connection to {{integrationDisplayName}}',
                  { integrationDisplayName }
                )}
              >
                <Icon type="plus" />
                {t('Add new connection')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {connectionByProductSlug.connections.map(connection => (
        <ConnectionCard
          key={connection.id}
          connection={connection}
          integrationProductInfo={integrationProductInfo}
          enterprise={activeEnterprise}
          canManageIntegrations={isEnterpriseAdmin}
          authUser={authUser}
          connectionsUpdated={reloadConnections}
          getActions={getJiraOnPremActions}
        />
      ))}
    </section>
  );
}

export default JiraOnPremIntegrationConfigurations;
